import { useEffect, useMemo, useState } from "react";

import { apiUrl, imageUrl } from '../../../config/api';
function normalizeArray(value) {
    return Array.isArray(value) ? value : [];
}

function extractListadoNombre(item) {
    if (typeof item === "string") {
        return item.trim();
    }

    return String(item?.jugador || item?.tecnico || item?.nombre || "").trim();
}

async function parseResponse(response) {
    if (!response.ok) {
        throw new Error(`Error! status: ${response.status}`);
    }

    const payload = await response.json();

    if (typeof payload === "string") {
        try {
            return JSON.parse(payload);
        } catch (error) {
            return payload;
        }
    }

    return payload;
}

async function fetchListado() {
    const [jugadoresResponse, tecnicosResponse] = await Promise.all([
        fetch(apiUrl('listado/listadoJugadores')),
        fetch(apiUrl('listado/listadoTecnicos')),
    ]);

    const [jugadores, tecnicos] = await Promise.all([
        parseResponse(jugadoresResponse),
        parseResponse(tecnicosResponse),
    ]);

    const jugadoresNombres = normalizeArray(jugadores)
        .map(extractListadoNombre)
        .filter(Boolean);

    const tecnicosNombres = normalizeArray(tecnicos)
        .map(extractListadoNombre)
        .filter(Boolean);

    const merged = [...jugadoresNombres, ...tecnicosNombres]
        .filter(Boolean)
        .filter((item, index, array) => array.indexOf(item) === index)
        .sort((a, b) => String(a).localeCompare(String(b), "es"));

    return {
        jugadores: jugadoresNombres,
        tecnicos: tecnicosNombres,
        combined: merged,
    };
}

async function fetchPerfil(nombre) {
    if (!nombre) {
        return null;
    }

        const response = await fetch(apiUrl(`jugador/${nombre}`));
    const payload = normalizeArray(await parseResponse(response));

    const jugadorProfile = payload.find((item) => item?.jugador) || null;
    const tecnicoProfile = payload.find((item) => !item?.jugador) || null;
    const principalProfile = jugadorProfile || tecnicoProfile || payload[0] || null;

    return {
        payload,
        jugadorProfile,
        tecnicoProfile,
        principalProfile,
        hasJugadorProfile: Boolean(jugadorProfile),
        hasTecnicoProfile: Boolean(tecnicoProfile),
        imageUrl: imageUrl(nombre),
    };
}

function resolveRole(requestedRole, profile) {
    if (!profile) return "jugador";

    if (requestedRole === "jugador" && profile.hasJugadorProfile) {
        return "jugador";
    }

    if (requestedRole === "tecnico" && profile.hasTecnicoProfile) {
        return "tecnico";
    }

    if (profile.hasJugadorProfile) return "jugador";
    if (profile.hasTecnicoProfile) return "tecnico";

    return "jugador";
}

function buildScopeOptions(profile, role) {
    if (!profile) {
        return [{ value: "carrera", label: "Carrera total" }];
    }

    const source = role === "tecnico"
        ? normalizeArray(profile.tecnicoProfile?.torneos)
        : normalizeArray(profile.jugadorProfile?.torneos);

    const torneoOptions = source
        .map((item) => item?.liga)
        .filter(Boolean)
        .filter((item, index, array) => array.indexOf(item) === index)
        .map((liga) => ({ value: liga, label: liga }));

    return [{ value: "carrera", label: "Carrera total" }, ...torneoOptions];
}

export default function useCompararData(initialSelection = {}) {
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
    const [isError, setIsError] = useState(false);

    const [listadoJugadores, setListadoJugadores] = useState([]);
    const [listadoTecnicos, setListadoTecnicos] = useState([]);
    const [listadoComparar, setListadoComparar] = useState([]);

    const [leftName, setLeftName] = useState(initialSelection.leftName || "");
    const [rightName, setRightName] = useState("");
    const [leftRole, setLeftRole] = useState(initialSelection.leftRole || "jugador");
    const [rightRole, setRightRole] = useState("jugador");
    const [leftScope, setLeftScope] = useState("carrera");
    const [rightScope, setRightScope] = useState("carrera");
    const [mode, setMode] = useState("promedios");

    const [leftProfile, setLeftProfile] = useState(null);
    const [rightProfile, setRightProfile] = useState(null);

    useEffect(() => {
        setLeftName(initialSelection.leftName || "");
        setLeftRole(initialSelection.leftRole || "jugador");
    }, [initialSelection.leftName, initialSelection.leftRole]);

    useEffect(() => {
        let isMounted = true;

        async function loadListados() {
            setIsLoadingList(true);
            setIsError(false);

            try {
                const payload = await fetchListado();
                if (!isMounted) return;

                setListadoJugadores(payload.jugadores);
                setListadoTecnicos(payload.tecnicos);
                setListadoComparar(payload.combined);
            } catch (error) {
                if (!isMounted) return;
                setIsError(true);
            } finally {
                if (isMounted) {
                    setIsLoadingList(false);
                }
            }
        }

        loadListados();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!leftName || !rightName) {
            setLeftProfile(null);
            setRightProfile(null);
            setIsLoadingProfiles(false);
            return;
        }

        let isMounted = true;

        async function loadProfiles() {
            setIsLoadingProfiles(true);
            setIsError(false);

            try {
                const [leftPayload, rightPayload] = await Promise.all([
                    fetchPerfil(leftName),
                    fetchPerfil(rightName),
                ]);

                if (!isMounted) return;

                setLeftProfile(leftPayload);
                setRightProfile(rightPayload);
            } catch (error) {
                if (!isMounted) return;
                setIsError(true);
                setLeftProfile(null);
                setRightProfile(null);
            } finally {
                if (isMounted) {
                    setIsLoadingProfiles(false);
                }
            }
        }

        loadProfiles();

        return () => {
            isMounted = false;
        };
    }, [leftName, rightName]);

    useEffect(() => {
        if (!leftProfile) {
            return;
        }

        setLeftRole((current) => resolveRole(current, leftProfile));
    }, [leftProfile]);

    useEffect(() => {
        if (!rightProfile) {
            return;
        }

        setRightRole((current) => resolveRole(current, rightProfile));
    }, [rightProfile]);

    const canUsePromedios = useMemo(() => {
        const leftCan = leftRole === "jugador" && leftProfile?.hasJugadorProfile;
        const rightCan = rightRole === "jugador" && rightProfile?.hasJugadorProfile;
        return Boolean(leftCan && rightCan);
    }, [leftRole, rightRole, leftProfile, rightProfile]);

    useEffect(() => {
        if (!canUsePromedios && mode === "promedios") {
            setMode("totales");
        }
    }, [canUsePromedios, mode]);

    const leftScopeOptions = useMemo(
        () => buildScopeOptions(leftProfile, leftRole),
        [leftProfile, leftRole]
    );

    const rightScopeOptions = useMemo(
        () => buildScopeOptions(rightProfile, rightRole),
        [rightProfile, rightRole]
    );

    useEffect(() => {
        if (!leftScopeOptions.some((option) => option.value === leftScope)) {
            setLeftScope("carrera");
        }
    }, [leftScopeOptions, leftScope]);

    useEffect(() => {
        if (!rightScopeOptions.some((option) => option.value === rightScope)) {
            setRightScope("carrera");
        }
    }, [rightScopeOptions, rightScope]);

    return {
        isLoading: isLoadingList || isLoadingProfiles,
        isError,
        listadoJugadores,
        listadoTecnicos,
        listadoComparar,
        leftName,
        setLeftName,
        rightName,
        setRightName,
        leftRole,
        setLeftRole,
        rightRole,
        setRightRole,
        leftScope,
        setLeftScope,
        rightScope,
        setRightScope,
        leftScopeOptions,
        rightScopeOptions,
        mode,
        setMode,
        canUsePromedios,
        leftProfile,
        rightProfile,
    };
}